import type { DashboardNeedsAttention } from '@common/types/insights';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Props {
    data: DashboardNeedsAttention;
}

export default function NeedsAttentionStrip({ data }: Props) {
    const theme = useTheme();
    const { intakesOutstanding, remindersExhausted, awaitingTreefort } = data;
    const allClear = intakesOutstanding === 0 && awaitingTreefort === 0;

    return (
        <Box
            sx={{
                border: `1px solid ${allClear ? theme.palette.success.light : theme.palette.warning.light}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: allClear ? '#f0fdf4' : '#fffbeb',
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                Needs attention
            </Typography>
            {allClear ? (
                <Typography variant="body2" sx={{ color: theme.palette.success.dark }}>
                    ✓ Nothing needs your attention — all intakes are moving.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {intakesOutstanding > 0 && (
                        <>
                            <Typography variant="body2">
                                ⚠ <strong>{intakesOutstanding.toLocaleString()}</strong>{' '}
                                {intakesOutstanding === 1
                                    ? 'intake form is outstanding (no response)'
                                    : 'intake forms are outstanding (no response)'}
                            </Typography>
                            {remindersExhausted > 0 && (
                                <Typography
                                    variant="body2"
                                    sx={{ pl: 2, color: theme.palette.text.secondary }}
                                >
                                    of which <strong>{remindersExhausted.toLocaleString()}</strong>{' '}
                                    {remindersExhausted === 1
                                        ? 'has exhausted all reminders'
                                        : 'have exhausted all reminders'}
                                </Typography>
                            )}
                        </>
                    )}
                    {awaitingTreefort > 0 && (
                        <Typography variant="body2">
                            ⏳ <strong>{awaitingTreefort.toLocaleString()}</strong>{' '}
                            {awaitingTreefort === 1
                                ? 'intake form is awaiting Treefort verification'
                                : 'intake forms are awaiting Treefort verification'}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
